import BillableSwitch from 'components/QuestionManagement/ComputeCapacityModal/BillableSwitch';
import DisableIdentifierProjectionSwitch
  from 'components/QuestionManagement/ComputeCapacityModal/DisableIdentifierProjectionSwitch';
import FederatedQuerySwitch from 'components/QuestionManagement/ComputeCapacityModal/FederatedQuerySwitch';
import PermissionService from 'components/Common/PermissionService';
import QuestionBillableSwitch from 'components/QuestionManagement/ComputeCapacityModal/QuestionBillableSwitch';
import React, { useEffect, useMemo } from 'react';
import useBillable from 'components/QuestionManagement/ComputeCapacityModal/useBillable';
import useCapacityManagement from 'components/QuestionManagement/ComputeCapacityModal/useCapacityManagement';
import useDisableIdentifierProjection
  from 'components/QuestionManagement/ComputeCapacityModal/useDisableIdentifierProjection';
import useFederatedQuery from 'components/QuestionManagement/ComputeCapacityModal/useFederatedQuery';
import useQuestionBillable from 'components/QuestionManagement/ComputeCapacityModal/useQuestionBillable';
import {
  Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  Grid, Radio, RadioGroup, Skeleton, Typography,
} from '@mui/material';
import {
  CLEAN_ROOMS_WITH_EXTENDED_CAPACITY,
  COMPUTE_CAPACITY,
} from 'utils/constants/question.constant';
import { CleanRoomTypes } from 'components/CleanRooms/constants';
import { HButton, HSpinner } from 'BaseComponents';
import {
  SESSION_STORAGE_KEYS, productPerm, userPermissions, userTypes,
} from 'utils/appConstants';
import {
  canSeeBillableFeatures,
  hasAnyBillableCompatibleCleanRooms,
  normalizeCleanRoomTypeKeys,
} from 'utils/permissionUtils';
import { habuColors } from 'Theme';
import { showAlert } from 'redux/actions/AlertActions';
import { updateQuestion } from 'redux/actions/QuestionBuilderActions';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchQuestion } from 'hooks/questionAPIs';
import { useParams } from 'react-router-dom';


const ComputeCapacityModal = ({
  open, onClose, questionId, crqId,
}) => {
  const dispatch = useDispatch();
  const questionFromBuilder = useSelector(state => state.questionBuilder.question);

  const { crId } = useParams();

  // Fetch full question details when opened from Question Management
  // (has questionId prop but no crId and question data not in Redux)
  // Don't fetch if we're in the Question Builder where data is already loaded
  const shouldFetchQuestion = !crId && !!questionId && questionFromBuilder?.ID !== questionId;
  const fetchedQuestionResult = useFetchQuestion(
    shouldFetchQuestion ? questionId : null,
    null, // no crId for standalone questions
  );

  // Use fetched question if available (from Question Management),
  // otherwise use builder question
  const question = (shouldFetchQuestion && fetchedQuestionResult?.data)
    || questionFromBuilder;

  const cleanRoomID = crId || sessionStorage.getItem(SESSION_STORAGE_KEYS.QB_CR_ID);
  const cleanroomDetail = useSelector(state => state.cleanRooms.detail);
  const cleanroomType = cleanroomDetail?.type;

  const isSuperUser = PermissionService.isAuthorized([userTypes.superuser]);
  const isDatasetMaterializationEnabled = PermissionService.isProductEnabled(
    [userPermissions.DatasetMaterialization],
  );
  const isDisableIdentifierProjectionEnabled = PermissionService.isProductEnabled([
    productPerm.DisableIdentifierProjection]);

  const federatedQueryEnabled = useMemo(() => {
    const isCrTypeValid = cleanroomType && ![
      CleanRoomTypes.LINKEDIN_CLEAN_ROOM,
      CleanRoomTypes.HABU_CONFIDENTIAL_COMPUTING_CLEAN_ROOM,
    ].includes(cleanroomType);

    return isSuperUser && !!crId && !!crqId && isCrTypeValid;
  }, [isSuperUser, crId, crqId, cleanroomType]);

  const isFQEnabledWithMaterialization = federatedQueryEnabled
    && (!isDatasetMaterializationEnabled);

  const disableIdentifierEnabled = useMemo(() => (
    isSuperUser && isDisableIdentifierProjectionEnabled && !!crId && !!crqId
  ), [isSuperUser, isDisableIdentifierProjectionEnabled, crId, crqId]);

  // Use cleanRoomID (which includes session storage) instead of just crId (URL param only)
  // This ensures correct mode detection when creating new CRQs where crId is in session storage
  const isTemplate = useMemo(() => !cleanRoomID, [cleanRoomID]); // Template if not in cleanroom
  const isCRQMode = useMemo(() => !!cleanRoomID, [cleanRoomID]); // CRQ if in cleanroom context
  const isEditMode = useMemo(() => {
    if (isTemplate) return !!questionId; // Template edit if has questionId
    return !!crqId; // CRQ edit if has crqId
  }, [isTemplate, questionId, crqId]);
  // CRQ (CleanRoom Questions) billable enabled: for questions inside cleanrooms
  const crqBillableEnabled = useMemo(() => (
    isCRQMode && canSeeBillableFeatures(cleanroomType)
  ), [isCRQMode, cleanroomType]);

  // Template question billable enabled: for standalone org-level template questions
  const templateQuestionBillableEnabled = useMemo(() => (
    isTemplate && hasAnyBillableCompatibleCleanRooms(question?.cleanRoomTypes)
  ), [isTemplate, question?.cleanRoomTypes]);

  // Federated query logic
  const {
    isFQFetching,
    isFQCreating,
    isFQUpdating,
    isFQCreated,
    isFQUpdated,
    isFederatedQuery,
    setIsFederatedQuery,
    saveFederatedQuery,
  } = useFederatedQuery({
    crId,
    crqId,
    enabled: isFQEnabledWithMaterialization,
  });

  // Capacity management logic
  const {
    isCapacityUpdating,
    isCapacityUpdated,
    currVal,
    setCurrVal,
    saveCapacity,
    initialVal,
  } = useCapacityManagement({
    crId, crqId, questionId, question,
  });

  const {
    isDIFetching,
    isDICreating,
    isDIUpdating,
    isDICreated,
    isDIUpdated,
    isDisableIdentifier,
    setIsDisableIdentifier,
    saveDisableIdentifier,
  } = useDisableIdentifierProjection({
    crId, crqId, enabled: disableIdentifierEnabled,
  });

  const {
    isBFetching,
    isBCreating,
    isBUpdating,
    isBUpserted,
    isBillable,
    setIsBillable,
    saveBillable,
    resetBillable,
  } = useBillable({
    crId: cleanRoomID, // Use cleanRoomID to include session storage
    crqId,
    enabled: crqBillableEnabled,
    isEditMode,
  });

  const {
    isQBFetching,
    isQBCreating,
    isQBCreated,
    isQuestionBillable,
    setIsQuestionBillable,
    saveQuestionBillable,
    resetQuestionBillable,
  } = useQuestionBillable({
    questionId,
    enabled: templateQuestionBillableEnabled && open,
  });

  useEffect(() => {
    const isFQActionComplete = isFQEnabledWithMaterialization ? (isFQCreated || isFQUpdated) : true;
    const isDIActionComplete = disableIdentifierEnabled ? (isDICreated || isDIUpdated) : true;
    const isBActionComplete = crqBillableEnabled ? (isBUpserted) : true;
    const isQBActionComplete = templateQuestionBillableEnabled ? isQBCreated : true;
    if (!!questionId && isCapacityUpdated && isFQActionComplete
      && isDIActionComplete && isBActionComplete && isQBActionComplete) {
      onClose();
    }
  }, [isCapacityUpdated, questionId, onClose, isFQEnabledWithMaterialization, isFQCreated,
    isFQUpdated, isDICreated, isDIUpdated, disableIdentifierEnabled, isBUpserted,
    crqBillableEnabled, templateQuestionBillableEnabled, isQBCreated, open]);

  const handleCancel = () => {
    if (crqBillableEnabled) {
      resetBillable();
    }
    if (templateQuestionBillableEnabled) {
      resetQuestionBillable();
    }
    onClose();
  };

  const handleSave = async () => {
    if (questionId) {
      // Case for Advanced Settings dropdown button in the question list
      saveCapacity();

      // Handle federated query in CRQ advanced settings
      if (isFQEnabledWithMaterialization) {
        saveFederatedQuery();
      }

      if (disableIdentifierEnabled) {
        saveDisableIdentifier();
      }

      if (crqBillableEnabled) {
        saveBillable();
      }

      // Save template question billable config
      if (templateQuestionBillableEnabled) {
        saveQuestionBillable();
      }
    }
    else {
      // Case for Advance Settings in Question Builder
      const hasCapacityChanged = currVal !== initialVal;

      if (hasCapacityChanged) {
        dispatch(updateQuestion({ computeCapacity: currVal }));
        dispatch(showAlert({ message: `Processing capacity updated for "${question.title}"`, type: 'success' }));
      }

      // Handle CRQ billable in create mode
      if (crqBillableEnabled) {
        saveBillable();
      }

      if (templateQuestionBillableEnabled) {
        // For template questions, always save the billable state to Redux when Save is clicked
        // This ensures it's available for useCreateCloneQuestion
        dispatch(updateQuestion({ pendingBillable: isQuestionBillable }));
        if (question?.ID) {
          // Existing template question - save immediately to API
          saveQuestionBillable();
        }
      }

      onClose();
    }
  };

  // Outside of Hybrid/TEE/LinkedIn only snowflake supports Compute Capacity
  // and they only support default or Large. Thus we filter out everything else
  // and only show these options for Hybrid/TEE/LinkedIn.

  // Check if extended capacity should be available
  // For clean room context: check the cleanroomType
  // For standalone questions: check if any compatible clean room type supports extended capacity
  const supportsExtendedCapacity = cleanroomType
    ? CLEAN_ROOMS_WITH_EXTENDED_CAPACITY.includes(cleanroomType)
    : (() => {
      // Map short form keys to full enum values
      const typeMap = {
        HybridV2: CleanRoomTypes.HYBRID_V2_CLEAN_ROOM,
        HabuConfidentialComputing: CleanRoomTypes.HABU_CONFIDENTIAL_COMPUTING_CLEAN_ROOM,
        LinkedIn: CleanRoomTypes.LINKEDIN_CLEAN_ROOM,
      };
      const normalizedKeys = normalizeCleanRoomTypeKeys(question?.cleanRoomTypes);
      return normalizedKeys.some(key => {
        const fullType = typeMap[key];
        return fullType && CLEAN_ROOMS_WITH_EXTENDED_CAPACITY.includes(fullType);
      });
    })();

  const getFilteredComputeCapacity = Object.values(COMPUTE_CAPACITY)
    .filter(item => ![
      COMPUTE_CAPACITY.MEDIUM.value,
      COMPUTE_CAPACITY.XLARGE.value,
      COMPUTE_CAPACITY.XXLARGE.value,
      COMPUTE_CAPACITY.XXXLARGE.value,
    ]
      .includes(item.value)
      || supportsExtendedCapacity);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      data-testid='compute-capacity-modal'
    >
      <DialogTitle variant='h3'>Advanced Question Settings</DialogTitle>
      <Divider />
      <DialogContent>
        {!currVal ? (
          <Skeleton variant='rectangular' height={220} />
        ) : (
          <Grid container>
            <Box mb={2} width='100%'>
              <Typography variant='subtitle1' mb={1}>
                Processing capacity needed to run the question (optional)
              </Typography>
              <Typography variant='body2' mb={1}>
                Every time the question runs, the selected capacity will be applied to available
                resources.
                <br />
                These settings can be edited even after publishing the question.
              </Typography>
              {!cleanRoomID && (
                <Alert
                  severity='warning'
                  variant='filled'
                  sx={{
                    color: habuColors.neutral.white,
                    backgroundColor: habuColors.signal.warning['70'],
                  }}
                >
                  <Typography variant='subtitle2'>
                    {`Clicking "Save" will change processing capacity for all associated CRQs to "${COMPUTE_CAPACITY[currVal]?.name}"`}
                  </Typography>
                </Alert>
              )}
            </Box>
            <Grid item xs={12} md={4} pl={4}>
              <RadioGroup>
                {getFilteredComputeCapacity.map(item => (
                  <FormControlLabel
                    key={item.name}
                    value={item.value}
                    control={<Radio checked={currVal === item.value} />}
                    color='primary'
                    onChange={e => setCurrVal(e.target.value)}
                    label={item.name}
                    componentsProps={{
                      typography: { variant: 'body2' },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Grid item xs={12} md={8} pr={4}>
              <Box bgcolor={habuColors.neutral['5']} p={2}>
                <Typography variant='subtitle2' mb={1}>
                  {COMPUTE_CAPACITY[currVal]?.subtitle}
                </Typography>
                <Typography variant='body2'>
                  {COMPUTE_CAPACITY[currVal]?.description}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
        {federatedQueryEnabled && (
          <>
            <Divider sx={{ my: 2 }} />
            <FederatedQuerySwitch
              isLoading={isFQFetching}
              isFederatedQuery={isFederatedQuery}
              setIsFederatedQuery={setIsFederatedQuery}
              isDatasetMaterializationEnabled={isDatasetMaterializationEnabled}
            />
          </>
        )}
        {
          disableIdentifierEnabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <DisableIdentifierProjectionSwitch
                isLoading={isDIFetching}
                isDisableIdentifier={isDisableIdentifier}
                setIsDisableIdentifier={setIsDisableIdentifier}
              />
            </>
          )
        }
        {
          crqBillableEnabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <BillableSwitch
                isLoading={isBFetching}
                isBillable={isBillable}
                setIsBillable={setIsBillable}
              />
            </>
          )
        }
        {
          templateQuestionBillableEnabled && !isCRQMode && (
            <>
              <Divider sx={{ my: 2 }} />
              <QuestionBillableSwitch
                isLoading={isQBFetching}
                isQuestionBillable={isQuestionBillable}
                setIsQuestionBillable={setIsQuestionBillable}
              />
            </>
          )
        }
      </DialogContent>
      <Divider />
      <DialogActions>
        <HButton variant='outlined' onClick={handleCancel}>Cancel</HButton>
        <HButton
          variant='contained'
          onClick={handleSave}
          disabled={
            isCapacityUpdating || !currVal
            || (isFQEnabledWithMaterialization && (isFQCreating || isFQUpdating))
            || isDICreating || isDIUpdating || isBCreating || isBUpdating || isQBCreating
          }
          endIcon={
            isCapacityUpdating || (isFQEnabledWithMaterialization && (isFQCreating || isFQUpdating))
              ? <HSpinner isButton /> : null
          }
        >
          Save
        </HButton>
      </DialogActions>
    </Dialog>
  );
};

export default ComputeCapacityModal;
